import { CdkAccordionModule } from "@angular/cdk/accordion";
import { CommonModule } from "@angular/common";
import { Component, Input, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import {
  filter,
  Observable,
  take
} from "rxjs";
import { AccordionComponent } from "../../../../components/common/accordion/accordion.component";
import { NoDataComponent } from "../../../../components/common/no-data/no-data.component";
import { SharedComponents } from "../../../../components/shared.module";
import { TestPrioritizationActions } from "../../../../core/ngrx/actions/test-prioritization.action";
import {
  selectLoading,
  selectMappingResponse,
  selectRecommendations,
} from "../../../../core/ngrx/selectors/test-prioritization.selectors";
import { UtilitiesService } from "../../../../core/services/utilities.service";
import { RecommendationCodeSnippetComponent } from "./recommendation-code-snippet/recommendation-code-snippet.component";

/**
 * Component to display recommendations based on test prioritization mapping.
 * Uses Angular CDK Accordion and NGRX for state management.
 */
@Component({
  selector: "app-recommendations",
  standalone: true,
  imports: [
    CdkAccordionModule,
    AccordionComponent,
    NoDataComponent,
    CommonModule,
    ...SharedComponents,
  ],
  templateUrl: "./recommendations.component.html",
  styleUrl: "./recommendations.component.scss",
})
export class RecommendationsComponent implements OnInit {
  expandedIndex = 0;
  mappingResponse$ = this.store.select(selectMappingResponse);
  recommendation$ = this.store.select(selectRecommendations);
  mappingId: any = "";
  loading: boolean = false;
  loading$!: Observable<boolean>;

  /**
   * List of items to be displayed in the recommendation accordion.
   * Each item includes title, component, and recommendations details.
   */
  @Input()
  items: any = [];

  /**
   * Constructor to inject required services.
   * @param store NGRX store for managing application state.
   * @param restService Service for making REST API calls.
   */
  constructor(
    private store: Store,
    private utilitiService: UtilitiesService
  ) {}

  /**
   * Angular lifecycle hook that runs on component initialization.
   * Subscribes to the mapping response and loads recommendation details.
   */
  ngOnInit() {
    this.loading$ = this.store.select(selectLoading);
    this.mappingResponse$
      .pipe(
        filter((response) => !!response),
        take(1)
      )
      .subscribe((response) => {
        console.log("Response received in component:", response);
        this.mappingId = response.id;
        this.createRecommendations();
      });

    this.loadRecomendationDetails();
  }

  /**
   * Triggers the recommendation generation process and handles its status updates.
   */
  createRecommendations() {
    this.utilitiService.dispatchIfEmpty(
      this.recommendation$,
      TestPrioritizationActions.loadRecommendations({
        mappingId: this.mappingId,
      })
    );
  }

  /**
   * Fetches recommendation details for the current mapping ID
   * and formats the data to be displayed in the accordion.
   */
  loadRecomendationDetails() {
    this.recommendation$.subscribe((item: any) => {
      this.items = item.data.map((item: any) => ({
        ...item,
        title: `Code Snippet ID: ${item.CodeSnippetID}`,
        component: RecommendationCodeSnippetComponent,
        recommendations: this.parseRecommendations(item.recommendations),
      }));
    });
  }

  /**
   * Parses recommendation strings into an array of strings.
   * Handles potential errors in JSON parsing.
   * @param recommendations JSON string containing recommendations.
   * @returns Array of parsed recommendations, or an empty array if parsing fails.
   */
  private parseRecommendations(recommendations: string): string[] {
    try {
      return JSON.parse(recommendations.replace(/'/g, '"'));
    } catch (error) {
      console.error("Failed to parse recommendations:", error);
      return [];
    }
  }
}
